import { is_gen_2, isWallDisplay, isBluTRV } from 'src/helpers/interface_helpers';
import { getTRVFirmwareVersion, isVendorSpecificFW } from 'src/helpers/firmware_helpers';
import { mobile } from 'src/services/mobile-rework';
import type { Device } from 'src/types/Device';
import { SLEEPING_TYPES, HALF_SLEEPING_TYPES } from 'src/constants';
import { compare } from 'compare-versions';
import { writable, derived, get } from 'svelte/store';
import type { Readable, Writable } from 'svelte/store';

import { allDiscovered } from '../features/discovered';
import { devices } from '../lists/devices';
import { firmwares, firmwares_gen_2, firmwares_gen_gble, updating_devices } from './process';
import { devices_settings, devices_status } from './status';

export const for_update: Readable<{
	[type: string]: { fwVersion: string; newFwVersion: string };
}> = derived(
	[
		devices,
		devices_status,
		devices_settings,
		firmwares,
		firmwares_gen_2,
		firmwares_gen_gble,
		updating_devices,
	],
	([
		$devices,
		$devices_status,
		$devices_settings,
		$firmwares,
		$firmwares_gen_2,
		$firmwares_gen_gble,
		$updating_devices,
	]) => {
		const _for_update = {};
		for (const id in $devices) {
			if (Object.prototype.hasOwnProperty.call($devices, id)) {
				const device: Device = $devices[id];
				const isGBLETrv = isBluTRV(device?.type); //needed this because it's ble device but it's represented as a gen 3 device

				// handle only first channel main device id
				if (!isGBLETrv && id.indexOf('_') > -1) {
					continue;
				}

				// can not update this device type... gen2 rpototype
				if (device.type === 'SHPSW04P') {
					continue;
				}

				// ignore sleeping devices
				// if (SLEEPING_TYPES.includes(device.type)) {
				// 	continue;
				// }

				// add updateing devices to the list to keep them shown in the update all list
				if (typeof $updating_devices[id] !== 'undefined') {
					_for_update[id] = device;
				}

				// ignore offline devices or devices with incomplete data
				if (
					!isGBLETrv &&
					(!$devices_status[id]?.online || typeof $devices_settings[id] === 'undefined')
				) {
					continue;
				}

				if (is_gen_2(device)) {
					const _firmware = $firmwares_gen_2[id];

					// special case for Wall Display adapting to Gen.2 versioning system:
					// more info in this Gitlab issue https://gitlab.allterco.net/Shelly/frontend/shelly-app-3/-/issues/739

					if (isWallDisplay(device.type) && $devices_settings[id]?.DeviceInfo?.ver) {
						// console.log('DeviceInfo fom a Wall Display, lets check its fw version via the "ver" parameter', $devices_settings[id]?.DeviceInfo)
						const customVersion = '1.1.2';
						const wallDisplayVersion = $devices_settings[id]?.DeviceInfo?.ver;
						if (wallDisplayVersion) {
							if (compare('' + wallDisplayVersion, customVersion, '<')) {
								_for_update[id] = {
									fwVersion: $devices_settings[id]?.sys?.device.fw_id,
									newFwVersion: customVersion,
								};
							}
						}
					}

					if (isGBLETrv) {
						const semVer = /\d+(\.\d+){2,}/gi;
						const currentFirmware =
							getTRVFirmwareVersion(device)?.match(semVer)?.toString() || '1.0.0'; //using default value because it's possible that the firmware version is not available
						const newFirmware =
							get(firmwares_gen_gble)?.[device.type]?.version.match(semVer)?.toString() ||
							'1.0.0';
						if (compare(currentFirmware, newFirmware, '<')) {
							_for_update[id] = {
								fwVersion: currentFirmware,
								newFwVersion: newFirmware,
							};
						}
					}

					if (
						// has stable version in last fw list
						typeof _firmware?.stable !== 'undefined' &&
						// version in device data is different than version in last fw list
						_firmware?.stable.build_id !== $devices_settings[id]?.sys?.device.fw_id
					) {
						_for_update[id] = {
							fwVersion: $devices_settings[id]?.sys?.device.fw_id,
							newFwVersion: $firmwares_gen_2[id]?.stable?.build_id,
						};
					}
				} else if (device?.gen === 'GBLE') {
					const newFwVersion = $firmwares_gen_gble[device.type]?.version;

					const versionInStatus =
						$devices_status[device.id]?.[`fwversion:${device.channel}`]?.version;

					const currentFirmware =
						typeof device?.fwID !== 'undefined'
							? device?.fwID
							: typeof versionInStatus !== 'undefined' && versionInStatus !== ''
								? versionInStatus
								: undefined; // if there is no firmware in device data, we will use the one from status

					const semVer = /\d+(\.\d+){2,}/gi;
					const fwVer = currentFirmware?.match(semVer)?.toString();
					const updateFwVer = newFwVersion?.match(semVer)?.toString();

					const hasUpdate =
						((typeof versionInStatus == 'undefined' ||
							versionInStatus == '0.0.0' ||
							versionInStatus == '') &&
							typeof fwVer == 'undefined') ||
						(typeof fwVer !== 'undefined' &&
							typeof updateFwVer !== 'undefined' &&
							fwVer !== updateFwVer);

					if (hasUpdate) {
						_for_update[id] = {
							fwVersion: fwVer,
							newFwVersion: updateFwVer,
						};
					}

					if (!hasUpdate) {
						delete _for_update[id];
					}
				} else {
					const _firmware = $firmwares?.[device.type] || $firmwares?.data?.[device.type];

					if (typeof _firmware !== 'undefined') {
						if (device.type === 'SHRGBW2') {
							if (
								_firmware.version.split('/')[0] !==
									$devices_settings[id]?.fw?.split('/')[0] &&
								$devices_status[id]?.update?.has_update
							) {
								_for_update[id] = {
									fwVersion: $devices_settings[id].fw,
									newFwVersion: $firmwares?.[device.type]?.version,
								};
							}
						} else {
							if (
								$devices_settings[id]?.fw?.split('/')[0] !==
								_firmware.version.split('/')[0]
							) {
								if (!isVendorSpecificFW($devices_settings?.[device.id], 'Solus')) {
									_for_update[id] = {
										fwVersion: $devices_settings[id].fw,
										newFwVersion: $firmwares?.[device.type]?.version,
									};
								}
							}
						}
					}
				}
			}
		}

		return _for_update;
	},
);

const create_pending_connection_devices = () => {
	//this store will keep rebooting devices, the point is that cloud or device didn't indicate somehow for rebooting(gen2 only). But for the sake of equality we did it manually. To remove id from this store we are listening in socket.ts for Shelly.Settings. Then there when rebooted id comes up we will remove it from array.
	const { subscribe, set, update } = writable(
		JSON.parse(localStorage.getItem('pending_connection_devices')) || [],
	);

	return {
		subscribe,
		set,
		add: (device) =>
			update((pending_connection_devices_devices) => {
				pending_connection_devices_devices.push(device);
				return [...new Set(pending_connection_devices_devices)]; //this is just in case we get this id 2 or more times
			}),
		remove: (device) =>
			update((pending_connection_devices_devices) => {
				let index_for_remove;
				pending_connection_devices_devices.forEach((id, i) => {
					if (id === device) {
						index_for_remove = i;
					}
				});
				pending_connection_devices_devices.splice(index_for_remove, 1);
				return pending_connection_devices_devices;
			}),
	};
};

export const pending_connection_devices = create_pending_connection_devices();
pending_connection_devices.subscribe((val) =>
	localStorage.setItem('pending_connection_devices', JSON.stringify(val)),
);

const create_z_wave_inclusion_statuses = () => {
	//this store will keep rebooting devices, the point is that cloud or device didn't indicate somehow for rebooting(gen2 only). But for the sake of equality we did it manually. To remove id from this store we are listening in socket.ts for Shelly.Settings. Then there when rebooted id comes up we will remove it from array.
	const { subscribe, set, update } = writable(
		JSON.parse(localStorage.getItem('z_wave_inclusion_statuses')) || {},
	);

	return {
		subscribe,
		set,
		add: (deviceID, status) =>
			update((z_wave_inclusion_devices_devices) => {
				z_wave_inclusion_devices_devices[deviceID] = status;
			}),
		remove: (deviceID) =>
			update((z_wave_inclusion_devices_devices) => {
				delete z_wave_inclusion_devices_devices[deviceID];
				return z_wave_inclusion_devices_devices;
			}),
	};
};

export const z_wave_inclusion_statuses = create_z_wave_inclusion_statuses();
z_wave_inclusion_statuses.subscribe((val) =>
	localStorage.setItem('z_wave_inclusion_statuses', JSON.stringify(val)),
);

export const cloud_disabled_devices: Readable<{ [type: string]: {} }> = derived(
	[allDiscovered, devices, devices_settings],
	([$allDiscovered, $devices, $devices_settings]) => {
		if (!mobile.isMobile) {
			return {};
		}
		const cloud_disabled = {};

		Object.entries($allDiscovered).map((e) => {
			try {
				if (!$devices[e[0]]) {
					return;
				}
				const scanData: {} = e[1];
				if (
					SLEEPING_TYPES.indexOf($devices[e[0]].type) > -1 ||
					HALF_SLEEPING_TYPES.indexOf($devices[e[0]].type) > -1
				) {
					return;
				}

				if (
					$devices?.[e[0]]?.cloud_online == false &&
					typeof cloud_disabled[e[0]] === 'undefined'
				) {
					cloud_disabled[e[0]] = { id: e[0], ...scanData };
				}

				if ($devices?.[e[0]]?.cloud_online == true && cloud_disabled[e[0]]) {
					delete cloud_disabled[e[0]];
				}
			} catch (error) {
				console.warn('device id', e[0]);
				console.error(error);
			}
		});
		console.log('🙅‍♂️ cloud disabled list on update', cloud_disabled);
		return cloud_disabled;
	},
);

export const addDeviceModal: Writable<boolean> = writable<boolean>(false);
// not sure if the undefined/string thing is very appropriate but we will go for it as not to have 2 stores for passing id and open statusIconExtract...

export const authLockedDeviceModal: Writable<string> = writable(undefined);

export const authenticateGen2DeviceModal: Writable<string> = writable(undefined);

type unauthorisedRequestDeviceData = {
	responseHeaders: string | null;
	payload: any;
	url: string;
};

export const unauthorisedRequests: Writable<{
	[type: string]: unauthorisedRequestDeviceData;
}> = writable<{ [type: string]: unauthorisedRequestDeviceData }>({});

export const cachedLists = writable();
